import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { VehicleImageUploader } from "../../components/VehicleImageUploader";

const EMPTY = {
  make: "",
  model: "",
  year: new Date().getFullYear(),
  price: 0,
  mileage: 0,
  body_type: "Sedan",
  fuel_type: "Gasoline",
  transmission: "Automatic",
  vin: "",
  color: "",
  stock_number: "",
  history_report_url: "",
  description: "",
  features: [],
  images: [],
  status: "available",
};

export default function AdminInventory() {
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [decoding, setDecoding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/vehicles");
    setVehicles(data || []);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (v) => {
    setEditing(v.id);
    setForm({ ...EMPTY, ...v });
    setOpen(true);
  };

  const decodeVin = async () => {
    if (!form.vin) { toast.error("Enter a VIN first"); return; }
    setDecoding(true);
    try {
      const { data } = await api.get(`/vin/decode/${form.vin}`);
      setForm((f) => ({
        ...f,
        make: data.make || f.make,
        model: data.model || f.model,
        year: Number(data.year) || f.year,
        body_type: data.body_type || f.body_type,
        fuel_type: data.fuel_type || f.fuel_type,
        transmission: data.transmission || f.transmission,
      }));
      toast.success("VIN decoded");
    } catch { toast.error("VIN decode failed"); }
    finally { setDecoding(false); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        features: typeof form.features === "string" ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : form.features,
        images: typeof form.images === "string" ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : form.images,
      };
      if (editing) await api.put(`/vehicles/${editing}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    await api.delete(`/vehicles/${id}`);
    load();
  };

  return (
    <AdminShell title="Inventory">
      <div className="flex justify-between mb-6">
        <div className="label-eyebrow">{vehicles.length} vehicles</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} className="btn-cta rounded-sm" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl bg-[#0B1324] border-white/10 max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Add"} Vehicle</DialogTitle>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2 flex gap-2">
                <div className="flex-1">
                  <Label className="text-sm text-slate-300">VIN</Label>
                  <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="bg-[#030712] border-white/10 h-10 mt-1" data-testid="vehicle-vin-input" />
                </div>
                <Button type="button" onClick={decodeVin} disabled={decoding} className="self-end btn-gold rounded-sm h-10" data-testid="decode-vin-btn">
                  <Sparkles className="w-4 h-4 mr-1" /> Decode
                </Button>
              </div>
              {[
                { k: "make", label: "Make" },
                { k: "model", label: "Model" },
                { k: "year", label: "Year", type: "number" },
                { k: "stock_number", label: "Dealer Stock #" },
                { k: "price", label: "Price ($)", type: "number" },
                { k: "mileage", label: "Mileage", type: "number" },
                { k: "color", label: "Color" },
                { k: "body_type", label: "Body type" },
                { k: "fuel_type", label: "Fuel type" },
                { k: "transmission", label: "Transmission" },
                { k: "history_report_url", label: "WakzyCheck History Report URL (optional)", full: true },
              ].map((f) => (
                <div key={f.k} className={f.full ? "md:col-span-2" : ""}>
                  <Label className="text-sm text-slate-300">{f.label}</Label>
                  <Input type={f.type || "text"} value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                    className="bg-[#030712] border-white/10 h-10 mt-1"
                    data-testid={`vehicle-${f.k}-input`}
                    placeholder={f.k === "history_report_url" ? "https://..." : undefined}
                  />
                </div>
              ))}
              <div>
                <Label className="text-sm text-slate-300">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#030712] border-white/10 h-10 mt-1" data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                    <SelectItem value="auction">Auction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-slate-300">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#030712] border-white/10 mt-1" rows={3} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-slate-300">Features (comma-separated)</Label>
                <Input value={Array.isArray(form.features) ? form.features.join(", ") : form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  className="bg-[#030712] border-white/10 h-10 mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-slate-300 mb-2 block">Vehicle Photos</Label>
                <VehicleImageUploader
                  value={Array.isArray(form.images) ? form.images : (form.images ? String(form.images).split(",").map((s) => s.trim()).filter(Boolean) : [])}
                  onChange={(arr) => setForm({ ...form, images: arr })}
                  vehicleId={form.id}
                />
              </div>
            </div>
            <Button onClick={save} className="btn-cta rounded-sm mt-4" data-testid="save-vehicle-btn">Save Vehicle</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {vehicles.map((v) => (
          <div key={v.id} className="surface rounded-md overflow-hidden hover-lift group relative" data-testid={`inv-vehicle-${v.id}`}>
            <div
              className="absolute top-0 left-0 right-0 h-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: "linear-gradient(90deg,transparent 0%,#e63946 25%,#d4af37 75%,transparent 100%)" }}
            />
            <div className="aspect-video bg-cover bg-center relative" style={{ backgroundImage: `url(${v.images?.[0] || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800"})` }}>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1324] via-transparent to-transparent opacity-70" />
              <div className="absolute top-2 left-2">
                <span className={`pill pill-${v.status === "available" ? "available" : v.status === "sold" ? "sold" : "pending"}`}>{v.status}</span>
              </div>
              <div
                className="absolute bottom-2 right-2 px-3 py-1 rounded text-sm font-heading"
                style={{ background: "linear-gradient(135deg,rgba(212,175,55,0.95),rgba(230,57,70,0.85))", color: "#0a0a0a", fontWeight: 800 }}
              >
                ${v.price.toLocaleString()}
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs text-slate-400">{v.year} · {v.color}</div>
              <div className="font-heading text-base mt-1 chrome-text inline-block">{v.make} {v.model}</div>
              <div className="text-xs text-slate-400 mt-1">{v.mileage?.toLocaleString()} mi · {v.fuel_type}</div>
              {v.stock_number && (
                <div className="text-[10px] text-[#D4AF37] mt-1 font-mono uppercase tracking-wider" data-testid={`inv-stock-${v.id}`}>
                  Stock #{v.stock_number}
                </div>
              )}
              <div className="font-mono text-[10px] text-slate-500 mt-1">{v.vin}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => startEdit(v)} className="rounded-sm bg-transparent border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid={`edit-vehicle-${v.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(v.id)} className="text-[#E63946] hover:text-[#E63946]" data-testid={`delete-vehicle-${v.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
